// @ts-nocheck
import React from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useSidebar } from '../../../contexts/SidebarContext';
import { useLocalization, type Language } from '../../../config/localization';
import HeaderOrganizationSelector from '../../ui/navigation/HeaderOrganizationSelector';
import { hasManagerAccess } from '../../../constants/roles';
import Icon, { ICONS } from '../../ui/icons';

interface OrganizationContextProps {
  selectedLanguage: Language;
}

export default function OrganizationContext({
  selectedLanguage
}: OrganizationContextProps) {
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useLocalization(selectedLanguage);
  const { userAccess, currentOrganization } = useOrganization();
  const { activeContext, setActiveContext } = useSidebar();

  const hasManagerAccessCheck = hasManagerAccess(userAccess?.currentRole || 'User');

  // Helper function to navigate while preserving orgId parameter
  // Uses location.search directly to get actual current URL state
  // (useSearchParams hook may have stale values after window.history.pushState)
  const navigateWithParams = (path: string) => {
    const currentParams = new URLSearchParams(location.search);
    const orgId = currentParams.get('orgId');
    if (orgId) {
      navigate(`${path}?orgId=${orgId}`);
    } else {
      navigate(path);
    }
  };

  const handleNavigation = (path: string) => {
    navigateWithParams(path);
  };

  const handleOrganizationInfoClick = () => {
    setActiveContext('organization');
    navigateWithParams('/organization/info');
  };

  const handleManagerClick = () => {
    setActiveContext('organization');
    navigateWithParams('/organization/manager');
  };

  const handlePromptsClick = () => {
    setActiveContext('organization');
    navigateWithParams('/organization/manager/prompts');
  };

  const handleCategoriesClick = () => {
    setActiveContext('organization');
    navigateWithParams('/organization/manager/categories');
  };

  const handleCorpPromptsClick = () => {
    setActiveContext('organization');
    navigateWithParams('/organization/manager/corp-prompts');
  };

  const handleBillingClick = () => {
    setActiveContext('organization');
    navigateWithParams('/organization/billing');
  };


  const handleHistoryClick = () => {
    setActiveContext('organization');
    navigateWithParams('/organization/manager/history');
  };

  const handleMyReferencesClick = () => {
    setActiveContext('organization');
    navigateWithParams('/organization/my-references');
  };

  const handleCollectionsClick = () => {
    setActiveContext('organization');
    navigateWithParams('/organization/collections');
  };


  return (
    <div className="h-full flex flex-col">
      {/* User Profile Section */}
      {user && (
        <div className="p-4 border-b border-gray-700/50">
          <h3 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wide">
{t.organizationContext?.profile || 'Profile'}
          </h3>
          <div className="flex items-center gap-3">
            <img
              src={user.imageUrl || '/default-avatar.png'}
              alt={user.firstName || 'User'}
              className="w-12 h-12 rounded-xl border-2 border-purple-500/30"
              onError={(e) => {
                e.currentTarget.src = '/default-avatar.png';
              }}
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white truncate">
                {user.firstName} {user.lastName}
              </div>
              <div className="text-xs text-gray-400 truncate">
                {user.emailAddresses?.[0]?.emailAddress}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Organization Selector */}
      <div className="p-4 border-b border-gray-700/50">
        <h3 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wide">
          {t.nav?.organization || 'Organization'}
        </h3>
        <HeaderOrganizationSelector selectedLanguage={selectedLanguage} />
      </div>

      {/* Organization Actions */}
      {currentOrganization && (
        <div className="flex-1 p-4">
          <h3 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wide">
            {t.nav?.actions || 'Actions'}
          </h3>
          <div className="space-y-2">
            {/* Organization Info */}
            <button
              onClick={handleOrganizationInfoClick}
              className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-blue-600/10 hover:border-blue-400/50 border transition-all text-blue-400 hover:text-blue-300 text-left ${
                location.pathname === '/organization/info' ? 'border-blue-400/50 bg-blue-600/10' : 'border-transparent'
              }`}
            >
              <Icon name={ICONS.INFO} size={20} className="text-current" />
              <div>
                <div className="text-sm font-medium">
                  {t.orgSelector?.organizationInfo || 'Organization Info'}
                </div>
                <div className="text-xs text-gray-500">
                  {t.nav?.organizationInfoDesc || 'View details and statistics'}
                </div>
              </div>
            </button>

            {/* Management - only for Admin/Manager */}
            {hasManagerAccessCheck && (
              <button
                onClick={handleManagerClick}
                className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-green-600/10 hover:border-green-400/50 border transition-all text-green-400 hover:text-green-300 text-left ${
                  location.pathname === '/organization/manager' ? 'border-green-400/50 bg-green-600/10' : 'border-transparent'
                }`}
              >
                <Icon name={ICONS.USERS} size={20} className="text-current" />
                <div>
                  <div className="text-sm font-medium">
                    {t.nav?.management || 'Management'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t.nav?.managementDesc || 'Manage users and settings'}
                  </div>
                </div>
              </button>
            )}

            {(userAccess?.isAdmin || hasManagerAccess(currentOrganization.role ?? 'User')) && (
              <button
                onClick={() => { setActiveContext('organization'); navigateWithParams('/organization/mcp'); }}
                className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-purple-600/10 hover:border-purple-400/50 border transition-all text-purple-400 hover:text-purple-300 text-left ${
                  location.pathname === '/organization/mcp' ? 'border-purple-400/50 bg-purple-600/10' : 'border-transparent'
                }`}
              >
                <Icon name={ICONS.LINK} size={20} className="text-current" />
                <div>
                  <div className="text-sm font-medium">{t.mcpServers.title}</div>
                  <div className="text-xs text-gray-500">{t.mcpServers.navDescription}</div>
                </div>
              </button>
            )}

            {/* Categories - available for all organization members */}
            <button
              onClick={handleCategoriesClick}
              className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-cyan-600/10 hover:border-cyan-400/50 border transition-all text-cyan-400 hover:text-cyan-300 text-left ${
                location.pathname === '/organization/manager/categories' ? 'border-cyan-400/50 bg-cyan-600/10' : 'border-transparent'
              }`}
            >
              <Icon name={ICONS.FOLDER} size={20} className="text-current" />
              <div>
                <div className="text-sm font-medium">
                  {t.nav?.categories || 'Categories'}
                </div>
                <div className="text-xs text-gray-500">
                  {t.nav?.categoriesDesc || 'Manage prompt categories'}
                </div>
              </div>
            </button>

            {/* Corporate Prompts - available to all org members */}
            <button
              onClick={handleCorpPromptsClick}
              className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-purple-600/10 hover:border-purple-400/50 border transition-all text-purple-400 hover:text-purple-300 text-left ${
                location.pathname === '/organization/manager/corp-prompts' ? 'border-purple-400/50 bg-purple-600/10' : 'border-transparent'
              }`}
            >
              <Icon name={ICONS.PROMPTS} size={20} className="text-current" />
              <div>
                <div className="text-sm font-medium">
                  {t.nav?.corpPrompts || 'References'}
                </div>
                <div className="text-xs text-gray-500">
                  {t.nav?.corpPromptsDesc || 'Manage organization references'}
                </div>
              </div>
            </button>

            {/* My References - available to all org members */}
            <button
              onClick={handleMyReferencesClick}
              className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-indigo-600/10 hover:border-indigo-400/50 border transition-all text-indigo-400 hover:text-indigo-300 text-left ${
                location.pathname === '/organization/my-references' ? 'border-indigo-400/50 bg-indigo-600/10' : 'border-transparent'
              }`}
            >
              <Icon name={ICONS.PROMPTS} size={20} className="text-current" />
              <div>
                <div className="text-sm font-medium">
                  {t.nav?.myReferences || 'My References'}
                </div>
                <div className="text-xs text-gray-500">
                  {t.nav?.myReferencesDesc || 'Personal references collection'}
                </div>
              </div>
            </button>

            {/* Prompts Old - only for Admin/Manager */}
            {hasManagerAccessCheck && (
              <button
                onClick={handlePromptsClick}
                className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-600/10 hover:border-gray-400/50 border transition-all text-gray-400 hover:text-gray-300 text-left ${
                  location.pathname === '/organization/manager/prompts' ? 'border-gray-400/50 bg-gray-600/10' : 'border-transparent'
                }`}
              >
                <Icon name={ICONS.PROMPTS} size={20} className="text-current" />
                <div>
                  <div className="text-sm font-medium">
                    {t.nav?.promptsOld || 'Prompts'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t.nav?.promptsOldDesc || 'Prompts system'}
                  </div>
                </div>
              </button>
            )}

            {/* Billing - only for Admin/Manager and when payment_method is not 'none' */}
            {hasManagerAccessCheck && currentOrganization?.payment_method !== 'none' && (
              <button
                onClick={handleBillingClick}
                className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-cyan-600/10 hover:border-cyan-400/50 border transition-all text-cyan-400 hover:text-cyan-300 text-left ${
                  location.pathname === '/organization/billing' ? 'border-cyan-400/50 bg-cyan-600/10' : 'border-transparent'
                }`}
              >
                <Icon name={ICONS.ZAPS} size={20} className="text-current" />
                <div>
                  <div className="text-sm font-medium">
                    {t.nav?.billing || 'Billing'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t.nav?.billingDesc || 'Purchase tokens'}
                  </div>
                </div>
              </button>
            )}


            {/* Collections */}
            <button
              onClick={handleCollectionsClick}
              className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-purple-600/10 hover:border-purple-400/50 border transition-all text-purple-400 hover:text-purple-300 text-left ${
                location.pathname === '/organization/collections' ? 'border-purple-400/50 bg-purple-600/10' : 'border-transparent'
              }`}
            >
              <Icon name={ICONS.COLLECTIONS} size={20} className="text-current" />
              <div>
                <div className="text-sm font-medium">
                  {t.nav?.collections || 'Collections'}
                </div>
                <div className="text-xs text-gray-500">
                  {t.nav?.collectionsDesc || 'Organize your images'}
                </div>
              </div>
            </button>

            {/* Generation History */}
            <button
              onClick={handleHistoryClick}
              className={`w-full flex items-center gap-3 p-3 rounded-lg hover:bg-orange-600/10 hover:border-orange-400/50 border transition-all text-orange-400 hover:text-orange-300 text-left ${
                location.pathname === '/organization/manager/history' ? 'border-orange-400/50 bg-orange-600/10' : 'border-transparent'
              }`}
            >
              <Icon name={ICONS.HISTORY} size={20} className="text-current" />
              <div>
                <div className="text-sm font-medium">
                  {t.nav?.history || 'History'}
                </div>
                <div className="text-xs text-gray-500">
                  {t.nav?.historyDesc || 'View generation history'}
                </div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* No Organization Selected State */}
      {!currentOrganization && (
        <div className="flex-1 p-4 flex items-center justify-center">
          <div className="text-center">
            <div className="mb-3">
              <Icon name={ICONS.BUILDING} size={48} className="text-gray-500 mx-auto" />
            </div>
            <div className="text-sm text-gray-400 mb-2">
              {t.workflow?.selectOrganization || 'Select Organization'}
            </div>
            <div className="text-xs text-gray-500 max-w-48">
              {t.workflow?.selectOrganizationDesc || 'Choose an organization from the selector above'}
            </div>
          </div>
        </div>
      )}

      {/* Logout Button */}
      <div className="p-4 border-t border-gray-700/50 mt-auto">
        <button
          onClick={() => signOut({ redirectUrl: '/' })}
          className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-600/10 hover:border-gray-400/50 border border-transparent transition-all text-gray-400 hover:text-gray-300 text-left"
        >
          <Icon name={ICONS.LOG_OUT} size={20} className="text-current" />
          <div>
            <div className="text-sm font-medium">
              {t.nav?.logout || 'Logout'}
            </div>
            <div className="text-xs text-gray-500">
              {t.settingsContext?.logoutDesc || 'Sign out of your account'}
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}